import api, { API_URL } from './api.ts';
import { isPlatformAvailabilityError, PlatformAvailabilityError } from './platformAvailability.ts';
export interface MemberProfile {
  cpf_cnpj: string;
  birth_date?: string;
  company_type?: string;
  income_value: number;
  address: string;
  address_number: string;
  complement?: string;
  province: string;
  postal_code: string;
  city?: string;
  state?: string;
}
export interface Member {
  id: number;
  name: string;
  email: string;
  phone: string;
  referral_code: string;
  status: string;
  provisioning_status?: string;
  profile: Partial<MemberProfile> | null;
  csrf_token: string;
}
export interface MemberReferral {
  id: number;
  clinic_name: string;
  status: string;
  created_at: string;
  contract_start: string | null;
  contract_end: string | null;
  partner_id?: number;
  contact_name?: string;
  email?: string;
  phone?: string;
}
export interface MemberCommission {
  id: number;
  referral_id: number;
  clinic_name: string;
  payment_id: string;
  due_date: string;
  payment_status: string;
  amount: number;
  status: string;
}
export interface MembersDashboard {
  partner: Member;
  summary: {
    clients: number;
    total_expected: number;
    total_pending: number;
    total_paid: number;
    total_refunded: number;
  };
  referrals: MemberReferral[];
  commissions: MemberCommission[];
  referrals_total: number;
  commissions_total: number;
}
const base = '/api/members';
export function resolveMembersBaseUrl(location: Pick<Location, 'hostname' | 'origin'>): string {
  return location.hostname === 'members.sminy.ai' ? location.origin : API_URL;
}
const connection = () => ({
  baseURL: resolveMembersBaseUrl(window.location)
});
export interface MemberDocument {
  id: string;
  type: string;
  status: string;
  onboarding_url?: string;
  description?: string;
  manual_action_required?: boolean;
  guidance?: string;
}
const csrf = (token: string) => ({
  ...connection(),
  headers: {
    'X-Members-CSRF': token
  }
});
export const membersApi = {
  register: async (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    marketing_opt_in?: boolean;
    marketing_consent_version?: string;
  }) => (await api.post<Member>(`${base}/register`, data, connection())).data,
  login: async (data: {
    email: string;
    password: string;
  }) => (await api.post<Member>(`${base}/login`, data, connection())).data,
  me: async () => (await api.get<Member>(`${base}/me`, connection())).data,
  logout: async (token: string) => api.post(`${base}/logout`, {}, csrf(token)),
  saveProfile: async (data: MemberProfile, token: string) => (await api.put<Member>(`${base}/profile`, data, csrf(token))).data,
  dashboard: async (offset = 0) => (await api.get<MembersDashboard>(`${base}/dashboard`, {
    ...connection(),
    params: {
      limit: 25,
      offset
    }
  })).data,
  refer: async (data: {
    code: string;
    clinic_name: string;
    contact_name: string;
    email: string;
    phone: string;
    consent: boolean;
  }) => (await api.post(`${base}/referrals`, data, connection())).data,
  onboarding: async () => (await api.get<{
    documents: MemberDocument[];
  }>(`${base}/onboarding`, connection())).data,
  provision: async (id: number) => api.post(`${base}/admin/partners/${id}/provision`),
  sync: async (id: number) => api.post(`${base}/admin/partners/${id}/sync`),
  partners: async (offset = 0) => (await api.get<{
    items: Member[];
    total: number;
  }>(`${base}/admin/partners`, {
    params: {
      limit: 25,
      offset
    }
  })).data,
  referrals: async (offset = 0) => (await api.get<{
    items: MemberReferral[];
    total: number;
  }>(`${base}/admin/referrals`, {
    params: {
      limit: 25,
      offset
    }
  })).data,
  assign: async (id: number, data: {
    clinic_id: number;
    contract_start: string;
  }) => (await api.post(`${base}/admin/referrals/${id}/assign`, data)).data
};
export function membersError(error: unknown): string {
  if (isPlatformAvailabilityError(error)) {
    return new PlatformAvailabilityError((error as PlatformAvailabilityError).code).message;
  }
  const response = (error as {
    response?: {
      status?: number;
      data?: {
        detail?: unknown;
      };
    };
  })?.response;
  if (response?.status === 503 || response?.status === 404) return 'O portal de parceiros ainda não está disponível. Tente novamente mais tarde.';
  if (response?.status === 401) return 'Seu acesso expirou. Entre novamente para continuar.';
  if (response?.status === 403) return 'Não foi possível autorizar esta ação. Atualize a página e tente novamente.';
  if (response?.status === 429) return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos.';
  if (response?.status === 422) return 'Confira os dados preenchidos e tente novamente.';
  if (response?.status === 409) return typeof response.data?.detail === 'string' ? response.data.detail : 'Este cadastro ou vínculo já existe. Confira os dados antes de continuar.';
  return 'Não foi possível concluir agora. Tente novamente.';
}
export const money = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
}).format(Number(value || 0));
export const memberDate = (value?: string | null) => value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—';
export function memberStatus(value: string): string {
  const labels: Record<string, string> = {
    registered: 'Cadastro incompleto',
    review_required: 'Precisa de revisão',
    PARTIALLY_REFUNDED: 'Estorno parcial',
    DELETED: 'Cancelada',
    creating: 'Cadastro em andamento',
    reconciliation_required: 'Requer conferência',
    blocked: 'Cadastro bloqueado',
    draft: 'Cadastro incompleto',
    pending: 'Pendente',
    pending_review: 'Em análise',
    approved: 'Aprovado',
    active: 'Ativo',
    rejected: 'Revisar cadastro',
    suspended: 'Suspenso',
    new: 'Nova indicação',
    lead: 'Aguardando contratação',
    assigned: 'Contratado',
    contracted: 'Contratado',
    expected: 'Prevista',
    awaiting_transfer: 'Aguardando repasse',
    paid: 'Repassada',
    refunded: 'Estornada',
    cancelled: 'Cancelada',
    overdue: 'Em atraso',
    PENDING: 'Em aberto',
    RECEIVED: 'Recebida',
    CONFIRMED: 'Confirmada',
    OVERDUE: 'Em atraso',
    REFUNDED: 'Estornada',
    RECEIVED_IN_CASH: 'Recebida em dinheiro',
    DUNNING_REQUESTED: 'Em cobrança',
    CHARGEBACK_REQUESTED: 'Em contestação'
  };
  return labels[value] || 'Em acompanhamento';
}
export function contractLastDay(value: string | null): string {
  if (!value) return '—';
  const day = new Date(`${value.slice(0, 10)}T12:00:00`);
  day.setDate(day.getDate() - 1);
  return day.toLocaleDateString('pt-BR');
}
